import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlashLink as Link } from './SlashLink';
import {
  getPreferredLevel,
  trackLevelIntent,
  LEVEL_SWITCHER_OPTIONS,
} from '../utils/levelPreference';
import { levelBankReady } from '../constants/levels';

/**
 * Compact level chooser — remembers preference and routes to that level’s hub.
 * Keep L1 as the default cash funnel; L2/L3 are one click away.
 */
export default function LevelSwitcher({
  className = '',
  variant = 'pills',
  navigateOnChange = true,
  showReadyHint = false,
}) {
  const navigate = useNavigate();
  const [active, setActive] = useState(() => getPreferredLevel('l1'));

  useEffect(() => {
    const sync = (event) => {
      const next = event?.detail?.level || getPreferredLevel('l1');
      setActive(next);
    };
    window.addEventListener('cfa-preferred-level', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('cfa-preferred-level', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const onPick = (id, href) => {
    trackLevelIntent(id, { source: 'level_switcher', path: href });
    setActive(id);
    if (navigateOnChange && href) navigate(href);
  };

  if (variant === 'links') {
    return (
      <nav className={`level-switcher level-switcher--links ${className}`.trim()} aria-label="Exam level">
        {LEVEL_SWITCHER_OPTIONS.map((opt) => (
          <Link
            key={opt.id}
            to={opt.href}
            className={active === opt.id ? 'is-active' : undefined}
            onClick={() => trackLevelIntent(opt.id, { source: 'level_switcher_link', path: opt.href })}
          >
            {opt.label}
            {showReadyHint && !levelBankReady(opt.id) ? ' · soon' : ''}
          </Link>
        ))}
      </nav>
    );
  }

  return (
    <div
      className={`level-switcher level-switcher--pills ${className}`.trim()}
      role="group"
      aria-label="I’m preparing for"
    >
      <span className="level-switcher-label">I’m preparing for</span>
      <div className="level-switcher-pills">
        {LEVEL_SWITCHER_OPTIONS.map((opt) => {
          const ready = levelBankReady(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              className={`level-switcher-pill${active === opt.id ? ' is-active' : ''}${
                !ready ? ' is-soon' : ''
              }`}
              aria-pressed={active === opt.id}
              onClick={() => onPick(opt.id, opt.href)}
            >
              {opt.short}
              {!ready ? <span className="level-switcher-soon">soon</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
